import {
  Heading,
  Text,
  HStack,
  VStack,
  Badge,
  Button,
  useColorModeValue,
  Box,
} from '@chakra-ui/react';
import { Idea, User } from '../../types';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { Card } from '../../../../components/Card';

interface IdeaCardProps {
  idea: Idea;
  users: User[];
  onComplete: () => void;
  onDelete: () => void;
  canDelete: boolean;
}

/**
 * Idea Card Component
 * Displays a single idea or fix
 */
export function IdeaCard({
  idea,
  users,
  onComplete,
  onDelete,
  canDelete,
}: IdeaCardProps) {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('gray.900', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  const getUserName = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    return user?.name || 'Unknown';
  };

  const formatDate = (date: Date | string | Timestamp | undefined) => {
    if (!date) return 'N/A';
    if (date instanceof Date) {
      return format(date, 'MMM d, yyyy');
    }
    if (date instanceof Timestamp) {
      return format(date.toDate(), 'MMM d, yyyy');
    }
    return format(new Date(date), 'MMM d, yyyy');
  };

  return (
    <Card borderWidth="1px" borderColor={borderColor} shadow="sm">
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between">
            <HStack spacing={2}>
              <Badge colorScheme={idea.type === 'idea' ? 'blue' : 'orange'}>
                {idea.type === 'idea' ? 'Idea' : 'Fix'}
              </Badge>
              <Badge colorScheme={idea.status === 'completed' ? 'green' : 'gray'}>
                {idea.status === 'completed' ? 'Completed' : 'New'}
              </Badge>
            </HStack>
          </HStack>
          <Heading size="md" color={headingColor}>
            {idea.title}
          </Heading>
          <Text color={textColor}>{idea.description}</Text>
          <Box fontSize="sm" color={textColor}>
            <Text>Created by: {getUserName(idea.createdBy)}</Text>
            <Text>Created: {formatDate(idea.createdAt)}</Text>
            {idea.status === 'completed' && idea.completedBy && (
              <>
                <Text>Completed by: {getUserName(idea.completedBy)}</Text>
                <Text>Completed: {formatDate(idea.completedAt)}</Text>
              </>
            )}
          </Box>
          <HStack spacing={2}>
            {idea.status === 'new' && (
              <Button size="sm" colorScheme="green" onClick={onComplete}>
                Mark as Completed
              </Button>
            )}
            {canDelete && (
              <Button size="sm" colorScheme="red" variant="ghost" onClick={onDelete}>
                Delete
              </Button>
            )}
          </HStack>
        </VStack>
    </Card>
  );
}

